import React, {
  Fragment,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  Typography,
} from '@mui/material';
import { IMessagePost } from './interfaces';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { MessageCard } from '../MessageCard';
import { CommentsSection } from '../CommentsSection';
import { formatLocaleDate } from '@helpers/dateHelpers';
import { getCurrentUserAsMember } from '@helpers/memberHelper';
import { ROUTES } from '@routes/routes';
import { ContextMenu, ContextMenuOption } from '@components/context-menu';
import DeleteIcon from '@mui/icons-material/Delete';
import FlagIcon from '@mui/icons-material/Flag';
import { useTranslation } from 'react-i18next';
import { DeleteFeedMessageDialog } from '@components/feed/dialogs/DeleteFeedMessageDialog';
import { Comment } from '../CommentsSection/interfaces';
import { Comment as FeedComment } from '@api/feeds/interfaces';
import {
  useDeleteCommentLikeMutation,
  useGetMessageComments,
  usePostCommentLikeMutation,
} from '@reactQuery/feeds/feed';
import { mapApiComments } from '@components/feed/ResourceMessageFeed/helpers';
import { UseQueryResult } from '@tanstack/react-query';
import { IPaginatedResourceEndpointResponse } from '@api/interfaces';
import { feedCommentsEmitter } from '@events/feedCommentsEvents';
import { useAuth } from '@context/AuthProvider';
import { SocialButtons } from '@components/SocialButtons';

export const MessagePost: React.FC<IMessagePost> = ({
  id,
  date,
  author,
  message,
  images,
  commentsCount = 0,
  likesCount = 0,
  likedByCurrentUser = false,
  onLikeClick,
  intitalComments = [],
}): JSX.Element => {
  const { t } = useTranslation();
  const { currentUser: authenticatedUser } = useAuth();
  const pendingCommentLikeIds = useRef<Set<number>>(new Set());
  const currentMemberIdRef = useRef<number | undefined>(
    authenticatedUser?.member?.id
  );
  const { mutate: postCommentLike } = usePostCommentLikeMutation();
  const { mutate: deleteCommentLike } = useDeleteCommentLikeMutation();

  currentMemberIdRef.current = authenticatedUser?.member?.id;
  const [page, setPage] = useState<number>(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const currentUser = getCurrentUserAsMember();
  const [comments, setComments] = useState<Comment[]>(intitalComments);

  const fromDate = useMemo(() => {
    if (!intitalComments || intitalComments.length === 0) {
      return new Date();
    }

    return intitalComments[intitalComments.length - 1].date;
  }, []);

  const {
    data,
    isLoading: isLoadingMoreComments,
    error,
    refetch: loadMoreComments,
  } = useGetMessageComments(
    {
      messageId: id,
      fromDate: fromDate,
      page: page,
      pageSize: 5,
    },
    { enabled: false }
  ) as UseQueryResult<IPaginatedResourceEndpointResponse<FeedComment>, unknown>;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    handleMenuClose();
    setShowDeleteDialog(true);
  };

  const handleLoadMore = async () => {
    try {
      const result = await loadMoreComments();

      if (result.data) {
        setPage((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Failed to load more comments', error);
    }
  };

  useEffect(() => {
    if (data?.data) {
      setComments((prev) => {
        return [...prev, ...mapApiComments(data.data)];
      });
    }
  }, [data?.data]);

  useEffect(() => {
    if (!intitalComments || intitalComments.length === 0) return;

    setComments((prev) => {
      const newOnes = intitalComments.filter(
        (comment) => !prev.some((existing) => existing.id === comment.id)
      );

      return [...newOnes, ...prev];
    });
  }, [intitalComments]);

  useEffect(() => {
    const handler = ({
      messageId,
      commentId,
    }: {
      messageId: number;
      commentId: number;
    }) => {
      if (messageId !== id) return;

      setComments((prev) => prev.filter((c) => c.id !== commentId));
    };

    feedCommentsEmitter.on('deleted', handler);
    return () => feedCommentsEmitter.off('deleted', handler);
  }, [id]);

  useEffect(() => {
    const handler = ({
      messageId,
      commentId,
      memberId,
      likesCount: nextLikesCount,
      liked,
    }: {
      messageId: number;
      commentId: number;
      memberId: number;
      likesCount: number;
      liked: boolean;
    }) => {
      if (messageId !== id) return;

      const isCurrentMember = memberId === currentMemberIdRef.current;

      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id !== commentId) {
            return comment;
          }

          return {
            ...comment,
            likesCount: nextLikesCount,
            likedByCurrentUser: isCurrentMember
              ? liked
              : comment.likedByCurrentUser,
          };
        })
      );
    };

    feedCommentsEmitter.on('likeUpdated', handler);
    return () => feedCommentsEmitter.off('likeUpdated', handler);
  }, [id]);

  const onCommentLikeClick = (commentId: number): void => {
    const comment = comments.find((item) => item.id === commentId);

    if (!comment || pendingCommentLikeIds.current.has(commentId)) {
      return;
    }

    const nextLiked = !comment.likedByCurrentUser;
    const previousLiked = comment.likedByCurrentUser;
    const previousCount = comment.likesCount;

    pendingCommentLikeIds.current.add(commentId);

    setComments((prev) =>
      prev.map((item) => {
        if (item.id !== commentId) {
          return item;
        }

        return {
          ...item,
          likedByCurrentUser: nextLiked,
          likesCount: Math.max(0, previousCount + (nextLiked ? 1 : -1)),
        };
      })
    );

    const mutation = nextLiked ? postCommentLike : deleteCommentLike;

    mutation(
      { messageId: id, commentId },
      {
        onError: () => {
          setComments((prev) =>
            prev.map((item) => {
              if (item.id !== commentId) {
                return item;
              }

              return {
                ...item,
                likedByCurrentUser: previousLiked,
                likesCount: previousCount,
              };
            })
          );
        },
        onSettled: () => {
          pendingCommentLikeIds.current.delete(commentId);
        },
      }
    );
  };

  return (
    <Fragment>
      <MessageCard>
        <CardHeader
          avatar={
            <Avatar src={ROUTES.images.avatar(author.slug)} alt={author.name} />
          }
          action={
            <Fragment>
              <IconButton
                aria-label="settings"
                size="small"
                onClick={handleMenuOpen}
              >
                <MoreVertIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                  }}
                />
              </IconButton>
              <ContextMenu
                anchorEl={anchorEl}
                open={anchorEl !== null}
                onClose={handleMenuClose}
              >
                {author.slug === currentUser?.slug ? (
                  <ContextMenuOption
                    onClick={handleDelete}
                    label={t('feed:buttons.deleteMessage')}
                    icon={DeleteIcon}
                  />
                ) : (
                  <ContextMenuOption
                    onClick={() => console.log('report message')}
                    label={t('feed:buttons.reportMessage')}
                    icon={FlagIcon}
                  />
                )}
              </ContextMenu>
            </Fragment>
          }
          title={author.name}
          subheader={formatLocaleDate(date)}
          titleTypographyProps={{
            fontWeight: 'bold',
          }}
          sx={{
            pb: 0,
          }}
        />
        <CardContent>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-line',
            }}
          >
            {message}
          </Typography>
        </CardContent>
        {images && (
          <CardMedia
            component="img"
            image={images[0].src}
            title={images[0].title}
            sx={{
              mb: '16px',
            }}
            // height="230px"
          />
        )}
        <SocialButtons
          numberOfLikes={likesCount}
          liked={likedByCurrentUser}
          onLikeClick={onLikeClick}
          numberOfComments={commentsCount}
          sx={{
            padding: '0 16px 16px',
          }}
        />
        <CommentsSection
          messageId={id}
          author={currentUser}
          comments={comments}
          commentsCount={commentsCount}
          loadMoreDisabled={isLoadingMoreComments}
          onLoadMore={handleLoadMore}
          isLoading={isLoadingMoreComments}
          onLikeClick={onCommentLikeClick}
        />
      </MessageCard>

      <DeleteFeedMessageDialog
        messageId={id}
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      />
    </Fragment>
  );
};
